import Vue from "*.vue";
import { ListModel } from "@/shared/models/listModel";
import { AxiosAdapter, AxiosInstance, AxiosRequestConfig } from "axios";
import {
  CheckedInCustomersOnAccUnitType,
  ReservationCheckInCustomerData,
  SetCheckedInCustomers,
} from "./CheckinTypes";
import { EVisitorIntegrationModel } from "@/shared/models/eVisitor";

export function getRuleIgnoreAdapter(defaultAdapter: AxiosAdapter) {
  return function (c: any) {
    return defaultAdapter(c).catch((settlement: any) => {
      if (settlement && settlement.response && settlement.response.data) {
        try {
          const data = JSON.parse(settlement.response.data);

          if (data.code === "RuleBroken") {
            return Promise.resolve({ ...settlement.response, handled: true });
          }
        } catch {
          /* do nothing */
        }
      }
      throw settlement;
    });
  };
}

export const loadReservationCustomers = (axios: AxiosInstance) => async (
  reservationId: number
): Promise<SetCheckedInCustomers> => {
  const guests = await axios.get<ListModel<CheckedInCustomersOnAccUnitType>>(
    `/reservations/${reservationId}/guests`,
    {
      params: {
        $top: 1000,
      },
    }
  );

  const res = new SetCheckedInCustomers(reservationId, false);
  res.payload = guests.data.result.map((x: CheckedInCustomersOnAccUnitType) => {
    const combined = Object.assign(new CheckedInCustomersOnAccUnitType(), x);

    combined.guests = x.guests.map((y: ReservationCheckInCustomerData) => {
      const g = Object.assign(new ReservationCheckInCustomerData(), y);
      g.eVisitorCheckInTime = g.eVisitorCheckInTime?.substr(0, 5) ?? null;
      g.eVisitorCheckoutTime = g.eVisitorCheckoutTime?.substr(0, 5) ?? null;
      g.eVisitorExpectedCheckoutTime =
        g.eVisitorExpectedCheckoutTime?.substr(0, 5) ?? null;
      g.isDirty = false;
      g.isEmpty = false;
      return g;
    });

    return combined;
  });

  return res;
};

export const checkIn = (axios: AxiosInstance) => async (payload: {
  sendData: SetCheckedInCustomers;
}) => {
  return await axios.post(`reservations/CheckInGuests`, payload.sendData);
};

export const checkInToEVisitor = (vm: Vue) => async (
  guests: ReservationCheckInCustomerData[],
  eVisitorIntegration: EVisitorIntegrationModel
) => {
  let allSucceded = true;

  for (const guest of guests) {
    if (guest.eVisitorIsCheckedIn || guest.eVisitorIsCheckedOut) {
      let messageKey = "EVisitor.CheckIn.Skipped";
      messageKey += guest.eVisitorIsCheckedOut ? ".AlreadyCheckedOut" : ".AlreadyCheckedIn";

      guest.eVisitorCheckinResult = {
        success: true,
        message: vm.$t(messageKey, { eVisitorServiceName: eVisitorIntegration.name }).toString(),
      };
      continue;
    }

    try {
      // revert to default behaviour (don't display toasts or anything)
      let config: AxiosRequestConfig | undefined;
      if (vm.axios.defaults.adapter) {
        config = {
          adapter: getRuleIgnoreAdapter(vm.axios.defaults.adapter),
        } as AxiosRequestConfig;
      }

      await vm.axios.post(
        `reservations/CheckInMultipleGuestsToEVisitor`,
        { guestIds: [guest.id] },
        config
      );

      guest.eVisitorCheckinResult = {
        success: true,
        message: vm.$t("EVisitor.CheckIn.Success", { eVisitorServiceName: eVisitorIntegration.name }).toString(),
      };
    } catch (e) {
      allSucceded = false;
      if (e.handled) {
        guest.eVisitorCheckinResult = {
          success: false,
          message:
            e.data.brokenRules
              ?.map((x: { description: string }) =>
                vm.$t(x.description).toString()
              )
              .filter((x: string) => !!x && x.length > 0)
              .join("\n") ?? "",
        };
      } else {
        throw e;
      }
    }
  }

  return allSucceded;
};
